/**
 * Утилита для звуковых и браузерных уведомлений о новых сообщениях в чате.
 *
 * Звук генерируется через Web Audio API (без файла) — короткий двухтональный сигнал.
 * Браузерные уведомления — через Notification API с запросом разрешения.
 */

import { isChatMuted } from './chatMute';

let audioCtx = null;

/**
 * Возвращает (и при необходимости создаёт) AudioContext.
 * Создаётся лениво, т.к. браузеры требуют user gesture для запуска аудио.
 */
function getAudioCtx() {
    if (audioCtx) return audioCtx;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    audioCtx = new AudioContextClass();
    return audioCtx;
}

/**
 * Воспроизводит короткое уведомление (два тона: 880 Гц → 1320 Гц).
 * Длительность ~250 мс, плавное затухание.
 */
export function playNotificationSound() {
    const ctx = getAudioCtx();
    if (!ctx) return;

    // Если контекст был приостановлен (autoplay policy) — пробуем возобновить
    if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;

    // Первый тон — 880 Гц (A5)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.value = 880;
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.15, now + 0.02);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.12);

    // Второй тон — 1320 Гц (E6), чуть позже
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.value = 1320;
    gain2.gain.setValueAtTime(0, now + 0.1);
    gain2.gain.linearRampToValueAtTime(0.15, now + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.25);
}

/**
 * Проверяет, поддерживает ли браузер Notification API.
 */
export function isNotificationSupported() {
    return typeof Notification !== 'undefined';
}

/**
 * Запрашивает разрешение на браузерные уведомления.
 * Вызывается по user gesture (например, при первом входе в чат).
 *
 * @returns {Promise<string>} — 'granted' | 'denied' | 'default'
 */
export async function requestNotificationPermission() {
    if (!isNotificationSupported()) return 'denied';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    try {
        return await Notification.requestPermission();
    } catch {
        return 'denied';
    }
}

/**
 * Показывает браузерное уведомление.
 *
 * @param {string} title — Заголовок (имя отправителя)
 * @param {string} body — Текст сообщения
 * @param {string} [chatId] — ID чата для перехода по клику
 */
export function showChatNotification(title, body, chatId) {
    if (!isNotificationSupported() || Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible') return;

    try {
        const notification = new Notification(title, {
            body: body || 'Новое сообщение',
            icon: '/favicon.ico',
            tag: chatId ? `chat-${chatId}` : 'chat-message',
            badge: '/favicon.ico',
        });

        // Клик по уведомлению — фокусируем вкладку
        notification.onclick = () => {
            window.focus();
            notification.close();
        };

        // Авто-закрытие через 5 секунд
        setTimeout(() => {
            try { notification.close(); } catch {}
        }, 5000);
    } catch (err) {
        console.error('[chatNotifications] Failed to show notification:', err);
    }
}

/**
 * Полное уведомление о новом сообщении: звук + браузерное уведомление.
 * Срабатывает только если вкладка неактивна (document.hidden).
 *
 * @param {Object} params
 * @param {string} params.senderName — Имя отправителя
 * @param {string} [params.preview] — Текст сообщения для превью
 * @param {string} [params.chatId] — ID чата
 * @param {boolean} [params.playSound=true] — Воспроизвести звук
 * @param {boolean} [params.showNotification=true] — Показать браузерное уведомление
 */
export function notifyNewMessage({
    senderName,
    preview,
    chatId,
    playSound = true,
    showBrowserNotification = true,
}) {
    // Только если вкладка неактивна
    if (!document.hidden) return;

    // Если чат в mute — отключаем звук и браузерные уведомления
    if (chatId && isChatMuted(chatId)) {
        return;
    }

    if (playSound) {
        playNotificationSound();
    }

    if (showBrowserNotification) {
        showChatNotification(senderName || 'Новое сообщение', preview, chatId);
    }
}
